import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { api, FormService, PagedSearchResults, TimeService } from '@bayclubs/utils';

import { campsenum } from '../../models/enums.generated';
import { camps } from '../../models/models.generated';
import { PurchaseApiService } from '../../services/api/purchase-api.service';
import { FiltersStorageService } from '../../services/filters-storage.service';
import { SelectedMember } from '../../models/filters/selected-member.model';
import { PurchaseListFilter } from '../../models/filters/purchase-list-filter.model';
import { FiltersHelperService } from '../../services/filters-helper.service';

@Component({
    selector: 'app-purchase-list',
    host: { 'data-testid': 'purchase-list-page' },
    templateUrl: './purchase-list.component.html',
    styleUrls: ['./purchase-list.component.scss'],
    standalone: false
})
export class PurchaseListComponent implements OnInit {
    public items: camps.CampPurchaseInfo[] = [];

    public searchResults: PagedSearchResults = new PagedSearchResults();

    public isBusy: boolean = false;

    public pageSize: number = 30;

    public searchForm: UntypedFormGroup;

    public sorting: string[] = ['finalized at', 'purchase number'];

    public filters: PurchaseListFilter;

    filteredMember: SelectedMember;

    constructor(
        private api: PurchaseApiService,
        private router: Router,
        private formBuilder: UntypedFormBuilder,
        private time: TimeService,
        private filtersHelperService: FiltersHelperService
    ) {
        this.filters = this.getFilter();
    }

    ngOnInit() {
        this.initForm(this.filters);
        this.onSearch(0);
    }

    initForm(filter: PurchaseListFilter) {
        this.searchForm = this.formBuilder.group({
            membershipId: [null, []],
            dateFrom: [filter.dateFrom, []],
            dateTo: [filter.dateTo, []],
            amountFrom: [filter.amountFrom, []],
            amountTo: [filter.amountTo, []],
            showFinalized: [filter.showFinalized, []],
            showRefunded: [filter.showRefunded, []],
            purchaseNumber: [filter.purchaseNumber, []],
            campTypes: [filter.campTypes, []],
            sorting: [filter.sorting, []]
        });
        this.filteredMember = filter.filteredMember;

        this.searchForm.valueChanges.pipe(debounceTime(500)).subscribe(() => {
            this.onSearch(0);
        });
    }

    public get noItems() {
        return this.items.length == 0;
    }

    public onScroll() {
        if (this.searchResults.canNextPage) {
            this.onSearch(this.searchResults.nextPageNumber);
        }
    }

    onSearch(numberOfPagesToSkip: number = 0) {
        this.isBusy = true;

        this.getCampsApiPromise(numberOfPagesToSkip).subscribe(result => {
            if (numberOfPagesToSkip === 0) {
                this.items = [];
            }

            if (result.isSuccess) {
                const filter: PurchaseListFilter = <PurchaseListFilter>{
                    filteredMember: this.filteredMember,
                    dateFrom: this.searchForm.controls['dateFrom'].value,
                    dateTo: this.searchForm.controls['dateTo'].value,
                    amountFrom: this.searchForm.controls['amountFrom'].value,
                    amountTo: this.searchForm.controls['amountTo'].value,
                    showFinalized: this.searchForm.controls['showFinalized'].value,
                    showRefunded: this.searchForm.controls['showRefunded'].value,
                    purchaseNumber: this.searchForm.controls['purchaseNumber'].value,
                    sorting: <camps.SortParameters>this.searchForm.controls['sorting'].value,
                    campTypes: this.searchForm.controls['campTypes'].value
                };

                this.filtersHelperService.mergeQueryParam(filter, 'PurchaseList');

                this.searchResults.pageNumber = result.pageNumber;
                this.searchResults.totalCount = result.totalCount;
                this.searchResults.totalPages = result.totalPages;
                this.searchResults.pages = Array<number>();

                for (var j = 1; j <= this.searchResults.totalPages; j++) {
                    this.searchResults.pages.push(j);
                }

                for (var i = 0; i < result.pageItems.length; i++) {
                    this.items.push(result.pageItems[i]);
                }
            }

            this.isBusy = false;
        });
    }

    public sortChanged() {
        this.onSearch(0);
    }

    private getCampsApiPromise(numberOfPagesToSkip: number): Observable<api.OperationPagedResult<camps.CampPurchaseInfo>> {
        let criteria = this.prepareRequest(numberOfPagesToSkip);
        return this.api.search(criteria, false);
    }

    private prepareRequest(numberOfPagesToSkip: number = 0): camps.CampPurchaseSearchRequest {
        const request = <camps.CampPurchaseSearchRequest>{
            take: this.pageSize,
            skip: numberOfPagesToSkip * this.pageSize,
            membershipId: this.searchForm.controls['membershipId'].value,
            dateFrom: this.time.toBcDateTime(this.searchForm.controls['dateFrom'].value),
            dateTo: this.time.toBcDateTime(this.time.endOfDay(this.searchForm.controls['dateTo'].value)),
            amountFrom: FormService.getAmountFromCurrencyMask(this.searchForm.controls['amountFrom'].value),
            amountTo: FormService.getAmountFromCurrencyMask(this.searchForm.controls['amountTo'].value),
            types: this.searchForm.controls['campTypes'].value,
            statuses: [],
            purchaseNumber: this.searchForm.controls['purchaseNumber'].value,
            sorting: this.searchForm.controls['sorting'].value,
            includePurchasesFromOldMemberships: true
        };

        if (this.searchForm.controls['showFinalized'].value == true) {
            request.statuses.push(campsenum.CampPurchaseStatus.Finalized);
        }

        if (this.searchForm.controls['showRefunded'].value == true) {
            request.statuses.push(campsenum.CampPurchaseStatus.Refunded);
        }

        return request;
    }

    public onItemSelected(item: camps.CampPurchaseInfo) {
        this.router.navigate(['purchases/' + item.id + '/details']);
    }

    public onMembershipSelected(item: SelectedMember) {
        if (item) {
            this.filteredMember = item;
        } else {
            this.filteredMember = null;
        }
    }

    private getFilter() {
        const defaultFilter: PurchaseListFilter = {
            filteredMember: null,
            dateFrom: null,
            dateTo: null,
            amountFrom: null,
            amountTo: null,
            showFinalized: true,
            showRefunded: true,
            purchaseNumber: null,
            campTypes: [],
            sorting: <camps.SortParameters>{ order: campsenum.SortOrder.Desc, fieldName: 'finalized at' }
        };

        const restoredFilter = this.filtersHelperService.restoreFilter<PurchaseListFilter>('PurchaseList');

        return restoredFilter ? restoredFilter : defaultFilter;
    }
}
